import { buildEmployeeListWhereFilter } from '@/employees/application/adapters/listEmployees';
import { useListCrewMembers } from '@/jobs/application/adapters/getCrewMembers';
import { useMemo } from 'react';
import { CrewField } from '../RaptorJobCreateModal';
import { useJobRoles } from '@/raptorjob/presentation/components/RaptorJobCreatePage/hook/useJobRoles';
import { useEmployeeSearch } from '@/employees/presentation/hooks';

const EMPLOYEE_QUERY_LIMIT = 100;

interface UseERPCrewSelectorProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  startDate?: Date | null;
}

export const useERPCrewSelector = ({ crewFields, setCrewFields, startDate }: UseERPCrewSelectorProps) => {
  const { jobRoles, supportedJobRoles, loadingJobRoles } = useJobRoles();
  const { debouncedSearchTerm: employeeDebouncedSearchTerm, setSearchTerm: setEmployeeSearchTerm } = useEmployeeSearch();

  const employeeWhereCause = useMemo(() => buildEmployeeListWhereFilter(employeeDebouncedSearchTerm), [employeeDebouncedSearchTerm]);

  const { crewMembers, loading: loadingEmployees } = useListCrewMembers(
    {
      where: employeeWhereCause,
      skip: 0,
      take: EMPLOYEE_QUERY_LIMIT
    },
    startDate ?? undefined,
    startDate ?? undefined
  );

  const buildEmployeeOptionsForRole = useMemo(() => {
    return (selectedRoleId: number | null) => {
      if (!crewMembers || selectedRoleId == null) return [];

      const uniqueCrewMembers = Array.from(new Map(crewMembers.map((e) => [e.id, e])).values());
      const filteredByRole = uniqueCrewMembers.filter(
        (employee) => selectedRoleId != null && (employee.jobRoles?.some((role) => role.id === selectedRoleId) ?? false)
      );

      return filteredByRole.map((employee) => ({
        id: employee.id,
        name: employee.name || `Employee ${employee.id}`,
        label: employee.name || `Employee ${employee.id}`,
        value: employee.id,
        status: employee.status || []
      }));
    };
  }, [crewMembers]);

  const handleDeleteCrewField = (id: string) => {
    setCrewFields(crewFields.filter((field) => field.rowId !== id));
  };

  const handleRoleChange = (rowId: string, roleId: number, roleName: string) => {
    const updatedCrewFields = crewFields.map((crew) =>
      crew.rowId === rowId ? { ...crew, roleId, roleName, employeeId: null, employeeName: '' } : crew
    );
    setCrewFields(updatedCrewFields);
    setEmployeeSearchTerm('');
  };

  const handleEmployeeSelect = (rowId: string, employeeId: number | null, employeeName: string, status?: string[]) => {
    if (!employeeId) {
      const updatedCrewFields = crewFields.map((crew) =>
        crew.rowId === rowId ? { ...crew, employeeId: null, employeeName: '', status: undefined } : crew
      );

      setCrewFields(updatedCrewFields);
      return;
    }

    const updatedCrewFields = crewFields.map((crew) => (crew.rowId === rowId ? { ...crew, employeeId, employeeName, status } : crew));
    setCrewFields(updatedCrewFields);
  };

  return {
    jobRoles,
    loadingJobRoles,
    employeeDebouncedSearchTerm,
    setEmployeeSearchTerm,
    crewMembers,
    loadingEmployees,
    buildEmployeeOptionsForRole,
    handleDeleteCrewField,
    handleRoleChange,
    handleEmployeeSelect,
    supportedJobRoles
  };
};
